/**
 * Contract review backend integration.
 *
 *   POST /api/contracts/reviews/extract          → source text (no LLM)
 *   POST /api/contracts/reviews/generate         → output-schema JSON
 *   POST /api/contracts/reviews/export/redlines  → .docx attachment
 *
 * The backend is a stateless FastAPI service. There is no client-side review
 * engine: if the backend is unreachable the UI degrades to a clear error and
 * retry, never a fabricated review.
 */
import { getIdToken } from "@/platform/firebase/auth";
import type {
  ContractReview,
  ContractReviewFeedback,
  ContractReviewRecord,
  ReviewRequest,
  ReviewFeedbackInput,
  RevisedRedlineProposal,
} from "./types";

const DEFAULT_BASE =
  process.env.NEXT_PUBLIC_CONTRACT_REVIEW_BACKEND || "http://localhost:8089";

/** Firebase ID token header for every authenticated backend call. */
async function authHeaders(): Promise<Record<string, string>> {
  const token = (await getIdToken()) ?? "";
  return { Authorization: `Bearer ${token}` };
}

interface ExtractResult {
  text: string;
  name: string;
  charCount: number;
  extracted: boolean;
}

/** Extract text from an uploaded contract file. */
export async function extractContractText(file: File): Promise<ExtractResult> {
  const form = new FormData();
  form.append("file", file);

  let res: Response;
  try {
    res = await fetch(`${DEFAULT_BASE}/api/contracts/reviews/extract`, {
      method: "POST",
      headers: await authHeaders(),
      body: form,
    });
  } catch {
    // Backend unreachable — client-side fallback for plain-text formats only;
    // binary formats degrade to an empty result so the UI prompts the user
    // rather than fabricating a review from mojibake.
    const lower = file.name.toLowerCase();
    if (lower.endsWith(".txt") || lower.endsWith(".csv") || lower.endsWith(".md")) {
      try {
        const text = (await file.text()).trim();
        if (text) return { text, name: file.name, charCount: text.length, extracted: true };
      } catch {
        /* fall through */
      }
    }
    return { text: "", name: file.name, charCount: 0, extracted: false };
  }

  if (!res.ok) {
    // Surface the backend's real reason (e.g. unsupported type, corrupt file)
    // instead of the generic "scanned or image-only" fallback.
    let detail = `Could not read that file (HTTP ${res.status}).`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* keep the status text */
    }
    throw new Error(detail);
  }

  const json = await res.json();
  const text = json?.data?.text ?? "";
  return {
    text,
    name: json?.data?.name || file.name,
    charCount: json?.data?.char_count ?? text.length,
    extracted: Boolean(json?.data?.extracted),
  };
}

/** Run the full clause-by-clause review. Throws with a user-facing message. */
export async function generateReview(input: ReviewRequest): Promise<ContractReview> {
  const form = new FormData();
  form.append("contract_text", input.contract_text);
  form.append("contract_type", input.contract_type);
  form.append("user_side", input.user_side);
  form.append("focus_areas", input.focus_areas);
  form.append("constraints", input.constraints);
  form.append("must_not_lose", input.must_not_lose);
  form.append("client_id", input.client_id);
  form.append("client_name", input.client_name);
  form.append("deadline", input.deadline);
  form.append("deal_context", input.deal_context);

  let res: Response;
  try {
    res = await fetch(`${DEFAULT_BASE}/api/contracts/reviews/generate`, {
      method: "POST",
      headers: await authHeaders(),
      body: form,
    });
  } catch {
    throw new Error(
      "Could not reach the contract review service. Make sure the backend is running, then retry.",
    );
  }

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* keep the status text */
    }
    throw new Error(detail);
  }

  const json = await res.json();
  const data = json?.data as ContractReview | undefined;
  if (!data || !Array.isArray(data.clauses)) {
    throw new Error("The review service returned an unexpected response.");
  }
  return data;
}

/** Ask the agent a follow-up question about a completed review. Throws on error. */
export async function followUp(
  review: ContractReview,
  question: string,
): Promise<{ answer: string; revised_redlines: RevisedRedlineProposal[] }> {
  let res: Response;
  try {
    res = await fetch(`${DEFAULT_BASE}/api/contracts/reviews/followup`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await authHeaders()) },
      body: JSON.stringify({ review, question }),
    });
  } catch {
    throw new Error("Could not reach the review service. Make sure the backend is running.");
  }

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* keep the status text */
    }
    throw new Error(detail);
  }

  const json = await res.json();
  const data = json?.data;
  if (!data || typeof data.answer !== "string") {
    throw new Error("The review service returned an unexpected response.");
  }
  return {
    answer: data.answer,
    revised_redlines: Array.isArray(data.revised_redlines) ? (data.revised_redlines as RevisedRedlineProposal[]) : [],
  };
}

export interface FollowUpStreamMeta {
  redlines: RevisedRedlineProposal[];
  bytes: number;
  events: number;
  contentType: string;
  status: number;
  truncated?: boolean;
}

/** Heuristic: flagged when a reply fell short of a natural end.
 *  A reply is truncated when the stream closed before a clean `done` event
 *  OR the text ends without terminal punctuation (and without a finished
 *  redline schema block).
 */
export function isReplyTruncated(text: string, meta?: FollowUpStreamMeta | null): boolean {
  const clean = (text ?? "").replace(/\s+$/, "");
  if (!clean) return false;
  if (meta && meta.truncated === false && /[.!?](\s|$)/.test(clean)) return false;
  const endsCleanly = /[.!?]["”'’)]*\s*$/.test(clean) || /REDLINE_END\s*$/.test(clean);
  return !endsCleanly;
}

interface FollowUpStreamHandlers {
  onPiece: (piece: string) => void;
  onDone: (meta: FollowUpStreamMeta) => void;
  onError: (message: string) => void;
}

/** Stream a follow-up answer (SSE) piece by piece. */
export async function followUpStream(
  review: ContractReview,
  question: string,
  handlers: FollowUpStreamHandlers,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${DEFAULT_BASE}/api/contracts/reviews/followup`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await authHeaders()) },
      body: JSON.stringify({ review, question }),
    });
  } catch {
    handlers.onError("Could not reach the review service. Make sure the backend is running.");
    return;
  }

  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* keep the status text */
    }
    handlers.onError(detail);
    return;
  }

  if (!res.body) {
    handlers.onError("This browser does not support streaming responses.");
    return;
  }

  // Graceful fallback: an old/plain JSON backend (or a non-streaming reply)
  // must still render instead of being silently dropped by the SSE parser.
  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    let json: { data?: { answer?: unknown; revised_redlines?: unknown }; detail?: unknown };
    try {
      json = await res.json();
    } catch {
      handlers.onError("Unexpected response from the review service.");
      return;
    }
    const data = json.data;
    if (data && typeof data.answer === "string") {
      handlers.onPiece(data.answer);
      handlers.onDone({
        redlines: Array.isArray(data.revised_redlines) ? (data.revised_redlines as RevisedRedlineProposal[]) : [],
        bytes: -1,
        events: 1,
        contentType,
        status: res.status,
      });
    } else if (json.detail) {
      handlers.onError(String(json.detail));
    } else {
      handlers.onError("Unexpected response from the review service.");
    }
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finished = false;
  let bytes = 0;
  let events = 0;
  let receivedDoneEvent = false;

  const settleDone = (redlines: RevisedRedlineProposal[]) => {
    if (!finished) {
      finished = true;
      handlers.onDone({
        redlines,
        bytes,
        events,
        contentType,
        status: res.status,
        truncated: !receivedDoneEvent,
      });
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      bytes += value.byteLength;
      buffer += decoder.decode(value, { stream: true });

      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const block = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const line = block.replace(/\r\n/g, "\n").trim();
        if (!line.startsWith("data:")) continue;
        events += 1;

        let evt: { type?: string; piece?: string; redlines?: RevisedRedlineProposal[]; message?: string };
        try {
          evt = JSON.parse(line.slice(5).trim());
        } catch {
          continue;
        }

        if ((evt.type === "piece" || evt.type === "delta") && typeof evt.piece === "string") {
          handlers.onPiece(evt.piece);
        } else if (evt.type === "done") {
          receivedDoneEvent = true;
          settleDone(Array.isArray(evt.redlines) ? evt.redlines : []);
        } else if (evt.type === "error") {
          settleDone([]);
          handlers.onError(String(evt.message ?? "The stream ended early."));
        }
      }
    }
  } finally {
    settleDone([]);
  }
}
export async function exportRedlines(review: ContractReview, filename?: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${DEFAULT_BASE}/api/contracts/reviews/export/redlines`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await authHeaders()) },
      body: JSON.stringify({ review, filename }),
    });
  } catch {
    throw new Error("Could not reach the export service. Make sure the backend is running.");
  }

  if (!res.ok) {
    let detail = `Export failed (HTTP ${res.status}).`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* keep the status text */
    }
    throw new Error(detail);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `${review.document_name || "contract-review"} - redlines.docx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Export the review as a Word report mirroring the in-browser redline page (Pro feature). */
export async function exportRedlinedContract(
  review: ContractReview,
  filename?: string,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${DEFAULT_BASE}/api/contracts/reviews/export/redlined-contract`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await authHeaders()) },
      body: JSON.stringify({ review, filename }),
    });
  } catch {
    throw new Error("Could not reach the export service. Make sure the backend is running.");
  }

  if (!res.ok) {
    let detail = `Export failed (HTTP ${res.status}).`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* keep the status text */
    }
    throw new Error(detail);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `${review.document_name || "contract-review"} - redlined contract.docx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface ShareReviewResult {
  token: string;
  created_at?: string;
  expires_at?: string;
  password_set?: boolean;
  allow_download?: boolean;
  revoked?: boolean;
}

export interface ShareSettings {
  password?: string;
  expiry_days?: number;
  allow_download?: boolean;
  revoked?: boolean;
}

/** Create (or return/update) a share token for a review the caller owns. */
export async function shareReview(reviewId: string, settings?: ShareSettings): Promise<ShareReviewResult> {
  let res: Response;
  try {
    const token = (await getIdToken()) ?? "";
    res = await fetch(`${DEFAULT_BASE}/api/contracts/reviews/share`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ review_id: reviewId, ...(settings ?? {}) }),
    });
  } catch {
    throw new Error("Could not reach the share service. Make sure the backend is running.");
  }

  if (!res.ok) {
    let detail = `Share failed (HTTP ${res.status}).`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* keep the status line */
    }
    throw new Error(detail);
  }

  return (await res.json()) as ShareReviewResult;
}

export interface SharedReviewResult {
  review: ContractReviewRecord | null;
  locked: boolean;
  requires_password: boolean;
  allow_download: boolean;
  shared_at?: string;
  expires_at?: string;
}

/** Fetch a public, read-only copy of a review by its share token (no auth).
 *  Password-protected links return locked:true without the review body. */
export async function fetchSharedReview(tokenValue: string): Promise<SharedReviewResult> {
  let res: Response;
  try {
    res = await fetch(`${DEFAULT_BASE}/api/contracts/reviews/shared/${encodeURIComponent(tokenValue)}`);
  } catch {
    throw new Error("Could not reach the review service. Make sure the backend is running.");
  }

  if (res.status === 410) {
    throw new Error("This shared review link is no longer valid (expired or revoked).");
  }
  if (!res.ok) {
    throw new Error(res.status === 404 ? "This shared review link is no longer valid." : `Could not load the review (HTTP ${res.status}).`);
  }

  const json = (await res.json()) as Partial<SharedReviewResult>;
  if (json.locked) {
    return {
      review: null,
      locked: true,
      requires_password: true,
      allow_download: Boolean(json.allow_download),
      shared_at: json.shared_at,
      expires_at: json.expires_at,
    };
  }
  if (!json?.review) throw new Error("This shared review is no longer available.");
  return {
    review: json.review,
    locked: false,
    requires_password: false,
    allow_download: Boolean(json.allow_download),
    shared_at: json.shared_at,
    expires_at: json.expires_at,
  };
}

/** Validate the share password and receive the locked review (no auth). */
export async function unlockSharedReview(
  tokenValue: string,
  password: string,
): Promise<SharedReviewResult> {
  let res: Response;
  try {
    res = await fetch(
      `${DEFAULT_BASE}/api/contracts/reviews/shared/${encodeURIComponent(tokenValue)}/unlock`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      },
    );
  } catch {
    throw new Error("Could not reach the review service. Make sure the backend is running.");
  }

  if (res.status === 401) {
    throw new Error("Incorrect password for this shared review.");
  }
  if (!res.ok) {
    throw new Error(`Could not unlock the review (HTTP ${res.status}).`);
  }

  const json = (await res.json()) as Partial<SharedReviewResult>;
  if (!json?.review) throw new Error("This shared review is no longer available.");
  return {
    review: json.review,
    locked: false,
    requires_password: false,
    allow_download: Boolean(json.allow_download),
    shared_at: json.shared_at,
    expires_at: json.expires_at,
  };
}

/** Download the redlined contract .docx from a share with download enabled. */
export async function downloadSharedReview(
  tokenValue: string,
  documentName: string,
  password?: string,
): Promise<void> {
  const url = new URL(`${DEFAULT_BASE}/api/contracts/reviews/shared/${encodeURIComponent(tokenValue)}/download-redlines`);
  if (password) url.searchParams.set("password", password);
  let res: Response;
  try {
    res = await fetch(url.toString());
  } catch {
    throw new Error("Could not reach the download service. Make sure the backend is running.");
  }

  if (!res.ok) {
    let detail = `Download failed (HTTP ${res.status}).`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* keep the status line */
    }
    throw new Error(detail);
  }

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = `${documentName || "contract-review"} - redlined contract.docx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

/** Submit per-section feedback on a shared review (viewer must be signed in). */
export async function submitReviewFeedback(
  tokenValue: string,
  input: ReviewFeedbackInput,
): Promise<ContractReviewFeedback> {
  let res: Response;
  try {
    res = await fetch(
      `${DEFAULT_BASE}/api/contracts/reviews/shared/${encodeURIComponent(tokenValue)}/feedback`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(await authHeaders()) },
        body: JSON.stringify(input),
      },
    );
  } catch {
    throw new Error("Could not reach the review service. Make sure the backend is running.");
  }

  if (!res.ok) {
    let detail = `Feedback failed (HTTP ${res.status}).`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* keep the status line */
    }
    throw new Error(detail);
  }

  const json = (await res.json()) as { feedback: ContractReviewFeedback };
  if (!json?.feedback?.id) throw new Error("The review service returned an unexpected response.");
  return json.feedback;
}

/** List the signed-in viewer's own feedback on a shared review. */
export async function fetchSharedReviewFeedback(tokenValue: string): Promise<ContractReviewFeedback[]> {
  let res: Response;
  try {
    res = await fetch(
      `${DEFAULT_BASE}/api/contracts/reviews/shared/${encodeURIComponent(tokenValue)}/feedback`,
      { headers: await authHeaders() },
    );
  } catch {
    throw new Error("Could not reach the review service. Make sure the backend is running.");
  }

  if (!res.ok) {
    let detail = `Could not load feedback (HTTP ${res.status}).`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* keep the status line */
    }
    throw new Error(detail);
  }

  const json = (await res.json()) as { feedback: ContractReviewFeedback[] };
  return Array.isArray(json?.feedback) ? json.feedback : [];
}

/** List feedback received on the caller's own reviews (optionally one review). */
export async function fetchOwnerFeedback(reviewId?: string): Promise<ContractReviewFeedback[]> {
  let res: Response;
  try {
    const url = new URL(`${DEFAULT_BASE}/api/contracts/reviews/feedback`);
    if (reviewId) url.searchParams.set("review_id", reviewId);
    res = await fetch(url.toString(), { headers: await authHeaders() });
  } catch {
    throw new Error("Could not reach the review service. Make sure the backend is running.");
  }

  if (!res.ok) {
    let detail = `Could not load feedback (HTTP ${res.status}).`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* keep the status line */
    }
    throw new Error(detail);
  }

  const json = (await res.json()) as { feedback: ContractReviewFeedback[] };
  return Array.isArray(json?.feedback) ? json.feedback : [];
}

/** Delete a feedback entry (author or review owner). */
export async function deleteReviewFeedback(feedbackId: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${DEFAULT_BASE}/api/contracts/reviews/feedback/${encodeURIComponent(feedbackId)}`, {
      method: "DELETE",
      headers: await authHeaders(),
    });
  } catch {
    throw new Error("Could not reach the review service. Make sure the backend is running.");
  }

  if (!res.ok) throw new Error(`Could not delete feedback (HTTP ${res.status}).`);
}

export interface FeedbackDecisionResult {
  feedback: ContractReviewFeedback;
  applied_clause_id?: string | null;
  applied_language?: string | null;
  review_url?: string;
}

/** Owner decides on external feedback: accept/decline, optionally applying it. */
export async function decideFeedback(
  id: string,
  decision: "accepted" | "declined",
  apply: boolean,
): Promise<FeedbackDecisionResult> {
  let res: Response;
  try {
    res = await fetch(`${DEFAULT_BASE}/api/contracts/reviews/feedback/${encodeURIComponent(id)}/decision`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await authHeaders()) },
      body: JSON.stringify({ decision, apply }),
    });
  } catch {
    throw new Error("Could not reach the review service. Make sure the backend is running.");
  }

  if (!res.ok) {
    let detail = `Could not update the feedback (HTTP ${res.status}).`;
    try {
      const body = await res.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* keep the status line */
    }
    throw new Error(detail);
  }

  const json = (await res.json()) as FeedbackDecisionResult;
  if (!json?.feedback?.id) throw new Error("The review service returned an unexpected response.");
  return json;
}

export { DEFAULT_BASE };
